import React from 'react'

import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getPropertyRateCashPrices } from './propertyCashPrices'
import useGhaVerifierPrice from 'hooks/useGhaVerifierPrice'

interface Props {
  rate: App.EnrichedPropertyRate
  /**
   * Opt-in bundles the customer currently has selected on this rate. Their cost is added on top of
   * the rate price, which is what the customer will be charged at checkout.
   */
  selectedBundles: Array<App.Property.OptionalBundle>
  /**
   * Metasearch discount to subtract from the rate price before adding bundles. Bedbank rates pass
   * their resolved discount; LPC rates leave this unset (their discount is already in the totals).
   */
  discountAmount?: number
  /** True on the rate Google priced, the only one whose displayed price the GHA verifier checks. */
  isGooglePricedRate?: boolean
}

/**
 * Cash price for a property rate tile, including any opt-in bundles the customer has selected.
 *
 * Opt-in bundle cost is applied at display time only — writing it back into `rate.totals` would
 * double-count it in every other consumer (margin, GTM, agent commission, price details). Points
 * and burn pricing deliberately exclude it too, since the burn calculation was quoted against the
 * hotel-only price; those render via `OfferPageBurnPriceDisplay` rather than this component.
 */
function PropertyRateOptionPrice(props: Props) {
  const { rate, selectedBundles, discountAmount = 0, isGooglePricedRate = false } = props

  const selectedBundlesTotalPrice = selectedBundles.reduce((total, bundle) => total + bundle.totalPrice, 0)
  const { displayPrice, displayedMemberPrice: memberPrice } = getPropertyRateCashPrices(rate, discountAmount, selectedBundlesTotalPrice)
  const price = useGhaVerifierPrice(
    displayPrice,
    !isGooglePricedRate,
  ) + selectedBundlesTotalPrice

  return <LuxPlusPriceGuard
    showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
    fallbackPrice={<PriceRowPrice
      data-testid="property-package-price"
      size="M"
      price={price}
      saleUnit="total"
    />}
  >
    <LuxPlusPriceStack
      size="M"
      testid="property-package-price"
      price={price}
      memberPrice={memberPrice ?? 0}
      saleUnit="total"
    />
  </LuxPlusPriceGuard>
}

export default PropertyRateOptionPrice
